import {
  registerViewComponent,
  registerDetailComponent,
  registerInputComponent,
  registerInlineComponent,
} from "@data-root/react";
import { DateInput } from "./components/inputs/DateInput";
import { DecimalInput } from "./components/inputs/DecimalInput";
import { EnumInput } from "./components/inputs/EnumInput";
import { EnumListInput } from "./components/inputs/EnumListInput";
import { ImageInput } from "./components/inputs/ImageInput";
import { LongTextInput } from "./components/inputs/LongTextInput";
import { NumberInput } from "./components/inputs/NumberInput";
import { PercentInput } from "./components/inputs/PercentInput";
import { RefInput } from "./components/inputs/RefInput";
import { LanguageView } from "./views/LanguageView";
import { FormView } from "./views/FormView";
import { TableView } from "./views/TableView";
import { TextInput } from "./components/inputs/TextInput";
import { MarkdownView } from "./views/MarkdownView";
import { GalleryView } from "./views/GalleryView";
import { DetailView } from "./views/DetailView";
import { MapView } from "./views/MapView";
import { DashboardView } from "./views/DashboardView";
import { DetailItem } from "./components/details";
import { RefRowsDetail } from "./components/details/RefRowsDetail";
import { VisionView } from "./views/VisionView";
import { RefDetail } from "./components/details/Ref";
import { ImageDetail } from "./components/details/ImageDetail";
import { ListView } from "./views/ListView";
import { RefRowsInput } from "./components/inputs/RefRows/RefRowsInput";
import { DeckView, DeckRows } from "@data-root/plugin-view-deck";
import { TimelineView, Timeline } from "@data-root/plugin-view-timeline";
import { CalendarView } from "@data-root/plugin-view-calendar";
import { ChartView } from "@data-root/plugin-view-chart";
import { CustomView } from "@data-root/plugin-view-custom";
import {
  ActivitiesDashboard,
  HarvestsDashboard,
  QualityDashboard,
} from "@data-root/customer-agroceler";
import { Dashboard1, Dashboard2, Dashboard3 } from "@data-root/customer-garces";
import { TextView } from "@data-root/plugin-view-text";

import { Detail } from "./components/inline/Detail";
import { Form } from "./components/inline/Form";
import { List } from "./components/inline/List";
import { Map } from "./components/inline/Map";
import { GalleryItems } from "./components/inline/Gallery";
import { Table } from "./components/inline/Table";

import { LatLongDetail, LatLongInput } from "@data-root/plugin-type-latlong";
import { JSONInput, JSONDetail } from "@data-root/plugin-type-json";
import { ListInput, ListDetail } from "@data-root/plugin-type-list";
import { PasswordInput } from "./components/inputs/PasswordInput";

import {
  PocketbaseDriver,
  PocketbaseInternalDriver,
} from "@data-root/plugin-data-provider-pocketbase";
import { LocalDriver } from "@data-root/plugin-data-provider-local";
// EADP-2/9: the agnostic Postgres internal + data drivers, from the client-safe entry
// (no server runtime - the tRPC routers are type-only there).
import {
  PostgresInternalDriver,
  PostgresDriver as PostgresDataDriver,
} from "@data-root/plugin-data-provider-postgres/client";

import { registerDriver, registerInternalDriver } from "@data-root/core";

registerDriver("Local", LocalDriver);
registerDriver("PocketBase", PocketbaseDriver);
registerInternalDriver("PocketBase", PocketbaseInternalDriver);

// EADP-2/9: register the Postgres internal + data drivers for the eigenapps DEMO build
// (internalDriverType='Postgres' / dataProvider='Postgres'), talking to the EADP-1 demo-driver
// server over the isolated demo Postgres. Registered SYNCHRONOUSLY (Core resolves the internal
// driver at construction, so it must exist before `new Core`). Harmless for the default
// PocketBase build: registered-but-unused unless VITE_INTERNAL_DRIVER_TYPE=Postgres selects it.
registerInternalDriver("Postgres", PostgresInternalDriver);
registerDriver("Postgres", PostgresDataDriver);

// PGlite data provider (Lane Q8, composes Lane G). "Local" stays the
// zero-dep default; a previewed/deployed app may declare table.type
// "PostgresLite" (a valid DatabaseType) to opt into a local Postgres-grade
// provider seeded from APP_PREVIEW seedData (still LOCAL - no network, no PB).
//
// @data-root/plugin-data-provider-postgres-lite is a private @data-root
// package (eigen monorepo Lane G1/G6) that requires CodeArtifact auth to
// install. It is NOT yet a dependency here. Until it is added to
// package.json, this registration is a GUARDED, OPTIONAL dynamic import:
// when the package is present the "PostgresLite" driver registers; when it
// is absent (the current state) the import fails softly and apps declaring
// "PostgresLite" simply have no driver for those tables (the Q6 preview
// runtime additionally FORCES every table to "Local", so the preview is
// unaffected). The import specifier is held in a variable + marked
// @vite-ignore so a build with the package absent does not fail to resolve.
//
// HOST follow-up to make this a hard (static) registration:
//   1. add "@data-root/plugin-data-provider-postgres-lite" to package.json
//      (CodeArtifact auth required) and pnpm install,
//   2. replace this IIFE with a static import + registerDriver("PostgresLite", ...).
const POSTGRES_LITE_PKG = "@data-root/plugin-data-provider-postgres-lite";
void (async () => {
  try {
    const mod = (await import(/* @vite-ignore */ POSTGRES_LITE_PKG)) as {
      PostgresLiteDriver?: unknown;
    };
    const driver = mod?.PostgresLiteDriver;
    if (!driver) {
      console.warn(
        `[plugins] ${POSTGRES_LITE_PKG} loaded but did not export PostgresLiteDriver; ` +
          'keeping "Local" as the only local data provider.'
      );
      return;
    }
    registerDriver("PostgresLite", driver as never);
    console.info('[plugins] Registered "PostgresLite" data provider (Lane Q8).');
  } catch {
    // Not installed (current state) - expected. "Local" remains the default.
    console.info(
      `[plugins] ${POSTGRES_LITE_PKG} not installed; "PostgresLite" data provider ` +
        'unavailable. "Local" remains the default. (Lane Q8: add the dep + CodeArtifact auth to enable.)'
    );
  }
})();

registerViewComponent("Vision", VisionView);
registerViewComponent("Language", LanguageView);
registerViewComponent("Markdown", MarkdownView);
registerViewComponent("Form", FormView);
registerViewComponent("Detail", DetailView);
registerViewComponent("Deck", DeckView);
registerViewComponent("List", ListView);
registerViewComponent("Gallery", GalleryView);
registerViewComponent("Table", TableView);
registerViewComponent("Dashboard", DashboardView);
registerViewComponent("Map", MapView);
registerViewComponent("Timeline", TimelineView);
registerViewComponent("Calendar", CalendarView);
registerViewComponent("Chart", ChartView);
registerViewComponent("Custom", CustomView);
registerViewComponent("GarcesDashboard1", Dashboard1);
registerViewComponent("GarcesDashboard2", Dashboard2);
registerViewComponent("GarcesDashboard3", Dashboard3);
registerViewComponent("AgrocelerActivitiesDashboard", ActivitiesDashboard);
registerViewComponent("AgrocelerHarvestsDashboard", HarvestsDashboard);
registerViewComponent("AgrocelerQualityDashboard", QualityDashboard);
registerViewComponent("Text", TextView);

registerInlineComponent("Deck", DeckRows);
registerInlineComponent("Detail", Detail);
registerInlineComponent("Form", Form);
registerInlineComponent("List", List);
registerInlineComponent("Map", Map);
registerInlineComponent("Timeline", Timeline);
registerInlineComponent("Gallery", GalleryItems);
registerInlineComponent("Table", Table);

registerInputComponent("RefRows", RefRowsInput);
registerInputComponent("Text", TextInput);
registerInputComponent("LongText", LongTextInput);
registerInputComponent("Number", NumberInput);
registerInputComponent("Decimal", DecimalInput);
registerInputComponent("Percent", PercentInput);
registerInputComponent("Date", DateInput);
registerInputComponent("Enum", EnumInput);
registerInputComponent("EnumList", EnumListInput);
registerInputComponent("Image", ImageInput);
registerInputComponent("Ref", RefInput);
registerInputComponent("Time", null as any);
registerInputComponent("DateTime", null as any);
registerInputComponent("LatLong", LatLongInput);
registerInputComponent("JSON", JSONInput);
registerInputComponent("List", ListInput);
registerInputComponent("Password", PasswordInput);

registerDetailComponent("RefRows", RefRowsDetail);
registerDetailComponent("Text", DetailItem);
registerDetailComponent("LongText", DetailItem);
registerDetailComponent("Number", DetailItem);
registerDetailComponent("Decimal", DetailItem);
registerDetailComponent("Percent", DetailItem);
registerDetailComponent("Date", DetailItem);
registerDetailComponent("Enum", DetailItem);
registerDetailComponent("EnumList", DetailItem);
registerDetailComponent("Image", ImageDetail);
registerDetailComponent("Ref", RefDetail);
registerDetailComponent("Time", null as any);
registerDetailComponent("DateTime", null as any);
registerDetailComponent("LatLong", LatLongDetail);
registerDetailComponent("JSON", JSONDetail);
registerDetailComponent("List", ListDetail);
